import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper
} from '@mui/material';
import axios from 'axios';
import buildingImage from './../../../Images/house.jpeg';
import CustomBottomNav from './CustomNav';
import { BASE_URL } from './../../Api/ApiUrls';

const Buy_Property_Map = () => {
  // State declarations
  const [properties, setProperties] = useState([]);
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Helper function to determine marker color based on property age
  const getMarkerColor = (createdAt) => {
    if (!createdAt) return '#808080'; // Default grey for undefined/null dates

    const createdDate = new Date(createdAt);
    const now = new Date();

    // Calculate difference in months
    const diffTime = Math.abs(now.getTime() - createdDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = diffDays / 30.44; // Average days in a month

    if (diffMonths <= 3) {
      return '#008000'; // Green (3 months or less)
    } else if (diffMonths <= 6) {
      return '#FFFF00'; // Yellow (3 to 6 months)
    } else if (diffMonths <= 12) {
      return '#FFA500'; // Orange (6 to 12 months)
    } else {
      return '#FF0000'; // Red (above 1 year / 12 months)
    }
  };

 
  // Helper function to format price
const formatPrice = (priceString) => {
  if (priceString === null || priceString === undefined) {
    return 'N/A';
  }

  // Ensure it's a string before calling .replace()
  const priceAsString = String(priceString);

  // Remove non-numeric characters except '.' and parse as float
  const numericPrice = parseFloat(priceAsString.replace(/[^0-9.]/g, ''));

  if (isNaN(numericPrice)) return 'N/A';

  if (numericPrice >= 10000000) { // Crores (100 lakhs)
    return `${(numericPrice / 10000000).toFixed(1)}Cr`;
  } else if (numericPrice >= 100000) { // Lakhs
    return `${(numericPrice / 100000).toFixed(1)}L`;
  } else if (numericPrice >= 1000) { // Thousands
    return `${(numericPrice / 1000).toFixed(1)}K`;
  } else {
    return numericPrice.toString();
  }
};


  // Initialize Ola Maps
  useEffect(() => {
    console.log('Initializing Ola Maps...');

    const loadOlaMaps = async () => {
      if (!window.OlaMaps) {
        console.log('Loading Ola Maps script...');
        const script = document.createElement('script');
        script.src = 'https://api.olamaps.com/v2/olamaps.js';
        script.async = true;
        script.onload = () => {
          console.log('Ola Maps script loaded successfully');
          initializeMap();
        };
        script.onerror = () => {
          console.error('Failed to load Ola Maps script');
        };
        document.body.appendChild(script);
      } else {
        console.log('Ola Maps already loaded, initializing map');
        initializeMap();
      }
    };

    const initializeMap = () => {
      if (!mapContainerRef.current || !window.OlaMaps) {
        console.log('Map container not ready or OlaMaps not available');
        return;
      }

      try {
        console.log('Creating new OlaMaps instance');
        const olaMaps = new window.OlaMaps({
          apiKey: 'Icq3iPdYarCdv3I9kEmWB07guY8EYD4R6nAB8CUu'
        });

        console.log('Initializing map with container:', mapContainerRef.current);
        const myMap = olaMaps.init({
          container: mapContainerRef.current,
          style: 'https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json',
          center: [78.4844811, 17.5366218], // [lng, lat] - Hyderabad
          zoom: 12
        });

        console.log('Map initialized successfully');
        setMap(myMap);
        myMap.on('load', () => {
          console.log('Map style fully loaded, setting mapLoaded to true');
          setMapLoaded(true);
        });

        myMap.on('error', (e) => {
          console.error('Map error:', e.error);
        });

      } catch (error) {
        console.error('Error initializing Ola Maps:', error);
      }
    };

    loadOlaMaps();

    return () => {
      if (map) {
        console.log('Cleaning up map instance');
        // Add any cleanup needed for Ola Maps
        // Note: For custom markers, you might need to iterate and remove them explicitly
        // if using the `Marker` class from Ola Maps.
        // For layer/source based markers, the useEffect cleanup for markers will handle it.
      }
    };
  }, []);

  // Fetch properties data
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const categoriesResponse = await axios.get(`${BASE_URL}/property-category/`);
        const categories = categoriesResponse.data;

        const propertiesResponse = await axios.get(`${BASE_URL}/property/`);
        const filtered = propertiesResponse.data.filter(item =>
          item.type && item.type.toLowerCase().includes("sell")
        );

        const parsed = filtered.map(item => {
          const parseCoord = (coord) => {
            if (!coord) return null;
            const cleaned = coord.replace(/"/g, '').replace(':', '.');
            return parseFloat(cleaned);
          };

          const matchedCategory = categories.find(
            cat => cat.category_id == item.category_id
          );

          const categoryName = matchedCategory ? matchedCategory.category : 'Property';

          const images = item.property_images?.length > 0
            ? item.property_images.map(img => `${BASE_URL}${img.image}`)
            : [buildingImage];

          return {
            id: item.property_id,
            title: categoryName,
            location: item.location || 'Not specified',
             price: `₹${item.price}`, // This is the display string
            rawPrice: String(item.price), // <-- ENSURE THIS IS A STRING
            date: item.created_at?.split('T')[0],
            facing: item.facing?.replace(/"/g, '') || 'N/A',
            area: item.site_area ? `${item.site_area} sq ft` : 'N/A',
            dimensions: item.roadwidth ? `${item.roadwidth} ft road` : 'N/A',
            listedBy: item.posted_by?.replace(/"/g, '') || 'Agent',
            lat: parseCoord(item.lat),
            lng: parseCoord(item.long),
            length: item.length,
            width: item.width,
            property_name: item.property_name,
            mobile_no: item.mobile_no,
            images: images,
            propertyData: item,
            createdAt: item.created_at // Add createdAt to the parsed property object
          };
        });

        setProperties(parsed);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    fetchProperties();
  }, []);

  // Update markers when properties change AND map is loaded
  useEffect(() => {
    if (!mapLoaded || !map || properties.length === 0) {
      console.log('Skipping marker update - map not ready or no properties or map style not loaded');
      return;
    }

    console.log('Updating markers with', properties.length, 'properties');

    // Remove existing markers
    markers.forEach(marker => {
      // If using Ola Maps Marker class, you'd do marker.remove()
      // If using layers/sources, remove them
      try {
        if (map.getLayer(marker.id)) {
          map.removeLayer(marker.id);
        }
        if (map.getSource(marker.id)) {
          map.removeSource(marker.id);
        }
      } catch (error) {
        console.error('Error removing marker:', error);
      }
    });
    setMarkers([]); // Clear markers state after removal

    const addMarkers = () => {
      const currentMarkers = [];
      properties
        .filter(property => property.lat && property.lng)
        .forEach(property => {
          const markerId = `marker-${property.id}`;
          const markerColor = getMarkerColor(property.createdAt); // Get color based on age
          const formattedPrice = formatPrice(property.rawPrice); // Get formatted price

          // Create a custom DOM element for the marker
          const el = document.createElement('div');
          el.className = 'custom-property-marker';
          el.style.backgroundColor = markerColor; // Apply background color to the circle
          el.style.borderRadius = '50%';
          el.style.width = '50px'; // Adjust size as needed
          el.style.height = '50px'; // Adjust size as needed
          el.style.display = 'flex';
          el.style.justifyContent = 'center';
          el.style.alignItems = 'center';
          el.style.color = 'white'; // Text color for the price
          el.style.fontWeight = 'bold';
          el.style.fontSize = '12px'; // Adjust font size as needed
          el.style.border = '2px solid white';
          el.style.cursor = 'pointer';
          el.textContent = formattedPrice;


          // Create an Ola Maps Marker instance
          const marker = new window.OlaMaps.Marker({
            element: el,
            anchor: 'center' // Anchor the marker in the center of the element
          })
            .setLngLat([property.lng, property.lat])
            .addTo(map);

          // Add click event listener to the custom marker element
          el.addEventListener('click', () => {
            console.log('Marker clicked:', property.id, property);
            alert(`Clicked on property: ${property.title}\nLocation: ${property.location}\nListed: ${property.date}\nPrice: ${property.price}`);
          });

          currentMarkers.push({ id: markerId, propertyId: property.id, markerInstance: marker });
        });
      setMarkers(currentMarkers);
    };

    // Add markers initially
    addMarkers();

    // Control visibility based on zoom level
    const handleZoom = () => {
      const zoom = map.getZoom();
      console.log('Current zoom level:', zoom);
      markers.forEach(m => {
        // Access the custom DOM element from the marker instance
        const el = m.markerInstance.getElement();
        if (zoom >= 14) { // Adjust this zoom level as needed
          el.style.display = 'flex'; // Show marker with text
          el.textContent = formatPrice(properties.find(p => p.id === m.propertyId)?.rawPrice);
        } else if (zoom >= 10) { // Show only the circle at intermediate zoom
            el.style.display = 'flex';
            el.textContent = ''; // Clear text, show only colored circle
            el.style.width = '15px'; // Smaller circle
            el.style.height = '15px';
            el.style.fontSize = '0'; // Hide text by setting font size to 0
        }
        else {
          el.style.display = 'none'; // Hide markers at very low zoom
        }
      });
    };

    map.on('zoomend', handleZoom);
    // Call handleZoom once initially to set correct visibility
    handleZoom();

    return () => {
      // Clean up markers when component unmounts or properties/map change
      markers.forEach(marker => {
        if (marker.markerInstance) {
          marker.markerInstance.remove();
        }
      });
      setMarkers([]);
      if (map) {
        map.off('zoomend', handleZoom);
      }
    };

  }, [map, mapLoaded, properties]); // Re-run when map, mapLoaded, or properties change


  // Fit bounds to show all markers if there are properties
  useEffect(() => {
    if (!mapLoaded || !map || properties.length === 0) {
      return;
    }

    const coordinates = properties
      .filter(p => p.lat && p.lng)
      .map(p => [p.lng, p.lat]);

    if (coordinates.length > 0) {
      try {
        console.log('Fitting bounds to show all markers');
        const minLng = Math.min(...coordinates.map(c => c[0]));
        const maxLng = Math.max(...coordinates.map(c => c[0]));
        const minLat = Math.min(...coordinates.map(c => c[1]));
        const maxLat = Math.max(...coordinates.map(c => c[1]));

        if (isFinite(minLng) && isFinite(maxLng) && isFinite(minLat) && isFinite(maxLat)) {
          map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
            padding: 50,
            maxZoom: 15
          });
        } else {
          console.warn('Invalid coordinates for fitBounds. Centering on first property.');
          map.flyTo({
            center: coordinates[0],
            zoom: 12
          });
        }

      } catch (error) {
        console.error('Error fitting bounds:', error);
        if (coordinates.length > 0) {
          map.flyTo({
            center: coordinates[0],
            zoom: 12
          });
        }
      }
    }
  }, [map, mapLoaded, properties]); // This effect should also depend on map, mapLoaded, properties

  return (
    <Box
      sx={{
        pb: 7,
        maxWidth: 480,
        mx: "auto",
        position: 'relative',
        backgroundColor: 'rgb(239, 231, 221)',
        minHeight: '100vh'
      }}
    >
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
          Properties for Sale
        </Typography>
      </Box>

      {/* Ola Map Container */}
      <Box sx={{ px: 2, pb: 10 }}>
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: 'calc(100vh - 150px)',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#f0f0f0'
          }}
        >
          {!mapLoaded && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <Typography>Loading map...</Typography>
            </Box>
          )}
        </div>
      </Box>

      {/* Bottom Navigation */}
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
        <CustomBottomNav />
      </Paper>
    </Box>
  );
};

export default Buy_Property_Map;